import type { SupabaseClient } from '@supabase/supabase-js';

/** One lift in the user's library (public.exercises, docs/decisions/0016). */
export type ExerciseRow = { id: string; name: string; garmin_category: string | null };

/** FIT exercise categories (as @garmin/fitsdk spells them) with readable labels. */
export const GARMIN_CATEGORIES: Array<[string, string]> = [
  ['benchPress', 'Bench press'],
  ['calfRaise', 'Calf raise'],
  ['cardio', 'Cardio'],
  ['carry', 'Carry'],
  ['chop', 'Chop'],
  ['core', 'Core'],
  ['crunch', 'Crunch'],
  ['curl', 'Curl'],
  ['deadlift', 'Deadlift'],
  ['flye', 'Flye'],
  ['hipRaise', 'Hip raise'],
  ['hipStability', 'Hip stability'],
  ['hipSwing', 'Hip swing'],
  ['hyperextension', 'Hyperextension'],
  ['lateralRaise', 'Lateral raise'],
  ['legCurl', 'Leg curl'],
  ['legRaise', 'Leg raise'],
  ['lunge', 'Lunge'],
  ['olympicLift', 'Olympic lift'],
  ['plank', 'Plank'],
  ['plyo', 'Plyo'],
  ['pullUp', 'Pull-up'],
  ['pushUp', 'Push-up'],
  ['row', 'Row'],
  ['run', 'Run'],
  ['shoulderPress', 'Shoulder press'],
  ['shoulderStability', 'Shoulder stability'],
  ['shrug', 'Shrug'],
  ['sitUp', 'Sit-up'],
  ['squat', 'Squat'],
  ['totalBody', 'Total body'],
  ['tricepsExtension', 'Triceps extension'],
  ['warmUp', 'Warm-up'],
];

/** Same normalisation as SQL public.exercise_key(): trim, collapse spaces, lower-case. */
export function exerciseKey(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

export function findExercise(all: ExerciseRow[], name: string): ExerciseRow | undefined {
  const k = exerciseKey(name);
  return k ? all.find((e) => exerciseKey(e.name) === k) : undefined;
}

/**
 * Autocomplete: names starting with the query first, then any word starting with it, then
 * anything containing it. Empty query → nothing (the field shows its placeholder instead).
 */
export function matchExercises(all: ExerciseRow[], query: string, limit = 6): ExerciseRow[] {
  const q = exerciseKey(query);
  if (!q) return [];
  const rank = (e: ExerciseRow) => {
    const k = exerciseKey(e.name);
    if (k.startsWith(q)) return 0;
    if (k.split(/[\s-]/).some((w) => w.startsWith(q))) return 1;
    return k.includes(q) ? 2 : 3;
  };
  return all
    .map((e) => [rank(e), e] as const)
    .filter(([r]) => r < 3)
    .sort((a, b) => a[0] - b[0] || a[1].name.localeCompare(b[1].name))
    .slice(0, limit)
    .map(([, e]) => e);
}

export async function listExercises(c: SupabaseClient): Promise<ExerciseRow[]> {
  const { data, error } = await c.from('exercises').select('id,name,garmin_category').order('name');
  if (error) throw error;
  return data as ExerciseRow[];
}

/** The existing exercise for this name (ignoring case/spacing), or a new one. */
export async function ensureExercise(c: SupabaseClient, name: string): Promise<ExerciseRow> {
  const { data, error } = await c.rpc('ensure_exercise', { p_name: name });
  if (error) throw error;
  return { id: data.id, name: data.name, garmin_category: data.garmin_category };
}

/** Renames everywhere (programs, history, PBs, the live session); 23505 = name taken. */
export async function updateExercise(c: SupabaseClient, id: string, name: string, garminCategory: string | null): Promise<ExerciseRow> {
  const { data, error } = await c.rpc('update_exercise', { p_exercise_id: id, p_name: name, p_garmin_category: garminCategory ?? '' });
  if (error) {
    if (error.code === '23505') throw new Error(`You already have an exercise called “${name.trim()}”.`);
    throw error;
  }
  return { id: data.id, name: data.name, garmin_category: data.garmin_category };
}

export type ExerciseSet = {
  id: string;
  session_id: string;
  set_index: number;
  actual_reps: number | null;
  actual_weight: number | null;
  completed_at: string;
  sessions: { title: string; started_at: string } | null;
};

/** Every completed set of one exercise, newest session first, grouped by session. */
export async function exerciseHistory(c: SupabaseClient, exerciseId: string, limit = 300) {
  const { data, error } = await c
    .from('session_sets')
    .select('id,session_id,set_index,actual_reps,actual_weight,completed_at, sessions(title,started_at)')
    .eq('exercise_id', exerciseId)
    .order('completed_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  const groups: Array<{ session_id: string; title: string; started_at: string; sets: ExerciseSet[] }> = [];
  for (const s of data as unknown as ExerciseSet[]) {
    let g = groups.find((x) => x.session_id === s.session_id);
    if (!g) {
      g = { session_id: s.session_id, title: s.sessions?.title ?? 'Workout', started_at: s.sessions?.started_at ?? s.completed_at, sets: [] };
      groups.push(g);
    }
    g.sets.push(s);
  }
  for (const g of groups) g.sets.sort((a, b) => a.set_index - b.set_index);
  return groups;
}
